import styled from '@emotion/styled';
import React, { useRef } from 'react';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import IncomingBooking from '../IncomingBooking';
import { useBookings } from '~Providers/BookingsProvider';
import useI18n from '~Translations';
import { GetIncomingBookings_operativeViewer_incomingBookingsForVenue } from '~Providers/BookingsProvider/api/__queries__';
import { useClickOutside } from '~Hooks';
import { Box } from '~Styles';

const Wrapper = styled(Box)`
  background-color: var(--colorBlueGray1100);
  padding: 16px;
  overflow-y: auto;
  min-width: 364px;
  border-left: 1px solid var(--colorBlueGray1000);
`;

const ModalContent = styled.div`
  padding: 24px;
  background: var(--colorBlueGray1200);
  border-radius: 8px;
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.2);
  width: 600px;
  max-width: calc(100% - 64px);
  margin: 0;
  height: fit-content;
  max-height: calc(100vh - 16px);
  overflow-y: auto;

  /** Push modal up so iPad keyboard doesn't cover the input field */
  @media (min-height: 496px) {
    max-height: none;
    overflow-y: visible;
    margin: 16px 0 0 0;
  }

  @media (min-height: 680px) {
    margin: 16px 0 10% 0;
  }
`;

interface Props {
  onAcceptBooking: (booking: GetIncomingBookings_operativeViewer_incomingBookingsForVenue) => void;
}

const IncomingBookings: React.FC<Props> = ({ onAcceptBooking }) => {
  const { i18n } = useI18n();
  const bookings = useBookings();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const clearHighlightedIncomingBooking = () => {
    bookings.actions.setHighlightedIncomingBooking(null);
  };

  // Use click outside hook to close the details panel, but ignore clicks inside modals
  useClickOutside(wrapperRef, clearHighlightedIncomingBooking, true);

  // If auto-accept is enabled and Baemingo Choice is not enabled, show a message instead of the incoming bookings list since there won't be any actionable bookings to display
  if (bookings.autoAcceptBookings && !bookings.baemingoChoiceEnabled) {
    return (
      <Wrapper
        ref={wrapperRef}
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        fluid
        style={{ height: '100%' }}>
        <Box style={{ textAlign: 'center' }}>
          <Heading color="var(--colorWhite)" level={3}>
            {i18n('Bookings.ClickToViewDetails')}
          </Heading>
        </Box>
      </Wrapper>
    );
  }

  return (
    <Wrapper ref={wrapperRef} display="flex" flexDirection="column">
      <Box display="flex" flexDirection="column" gap="xs">
        <Heading level={3} bottom={0} color="var(--colorWhite)">
          {i18n('Bookings.IncomingBookings')}
        </Heading>

        {bookings.incomingLoading ? (
          <Paragraph color="var(--colorBlueGray200)" level={3}>
            {i18n('Bookings.LoadingBookings')}
          </Paragraph>
        ) : bookings.incoming.length === 0 ? (
          <Paragraph color="var(--colorBlueGray200)" level={3}>
            {i18n('Bookings.NoPendingBookings')}
          </Paragraph>
        ) : (
          bookings.incoming.map((booking) => {
            return (
              <IncomingBooking
                key={booking.id}
                booking={booking}
                onAcceptBooking={onAcceptBooking}
              />
            );
          })
        )}
      </Box>
    </Wrapper>
  );
};

export default IncomingBookings;
